import type {
  AddStaffFieldStates,
  AddStaffFormMode,
  AddStaffValidatedField,
} from "./addStaffPanel.contract";
import { getAddStaffFieldVisibility } from "./addStaffPanel.fieldVisibility.logic";

export type AddStaffSectionName =
  | "personal-details"
  | "contract"
  | "terms"
  | "vacation"
  | "payment";

export type AddStaffSectionCompletion = Readonly<
  Record<AddStaffSectionName, boolean>
>;

interface VisibleSectionsParams {
  readonly employmentType: string | undefined;
  readonly mode: AddStaffFormMode;
  readonly salaryType: string | undefined;
}

type AddStaffSectionFields = Readonly<
  Record<AddStaffSectionName, readonly AddStaffValidatedField[]>
>;

const toSectionFields = <TFields extends AddStaffSectionFields>(
  sectionFields: TFields &
    Record<
      Exclude<AddStaffValidatedField, TFields[AddStaffSectionName][number]>,
      never
    >,
): AddStaffSectionFields => sectionFields;

const SECTION_FIELDS = toSectionFields({
  "personal-details": [
    "phoneNumber",
    "firstName",
    "lastName",
    "email",
    "personalIdentityNumber",
  ],
  contract: [
    "employeeNumber",
    "role",
    "teamIds",
    "employmentType",
    "employeeCategory",
    "startDate",
    "endDate",
    "salaryType",
    "salaryAmount",
    "workPercentage",
    "taxTable",
  ],
  terms: ["agreement", "noticeDays", "vacationDays", "workplace"],
  vacation: [
    "maxSickLeaveDays",
    "maxCareOfChildDays",
    "maxParentalLeaveDays",
    "advanceVacationDays",
  ],
  payment: ["paymentType", "paymentMethodValue"],
});

/**
 * The sections a panel shows, in the order they are rendered. A section is
 * shown while it has a field to show: the contract terms section only for
 * employment types that have contract terms, the vacation section only for
 * salary models that earn vacation days, and the personal details and payment
 * sections only while the form asks for those details.
 */
export const getVisibleAddStaffSections = ({
  employmentType,
  mode,
  salaryType,
}: VisibleSectionsParams): AddStaffSectionName[] => {
  const fieldVisibility = getAddStaffFieldVisibility({
    employmentType,
    mode,
    salaryType,
  });
  const sections: AddStaffSectionName[] = [];
  if (fieldVisibility.showPersonalDetails) {
    sections.push("personal-details");
  }
  sections.push("contract");
  if (fieldVisibility.showContractTerms) {
    sections.push("terms");
  }
  if (fieldVisibility.showVacationEarningDays) {
    sections.push("vacation");
  }
  if (fieldVisibility.showPaymentMethod || fieldVisibility.showPaymentType) {
    sections.push("payment");
  }
  return sections;
};

export const getAddStaffSectionCompletion = (
  fieldStates: AddStaffFieldStates,
): AddStaffSectionCompletion => {
  const isSectionComplete = (section: AddStaffSectionName): boolean =>
    SECTION_FIELDS[section].every((field) => fieldStates[field].valid);

  return {
    "personal-details": isSectionComplete("personal-details"),
    contract: isSectionComplete("contract"),
    terms: isSectionComplete("terms"),
    vacation: isSectionComplete("vacation"),
    payment: isSectionComplete("payment"),
  };
};

export const hasCompletedAddStaffSections = ({
  sectionCompletion,
  visibleSections,
}: {
  readonly sectionCompletion: AddStaffSectionCompletion;
  readonly visibleSections: readonly AddStaffSectionName[];
}): boolean => visibleSections.every((section) => sectionCompletion[section]);
